'use client';

import React, { useState, useEffect } from 'react';
import { fetchVendorPriceLagAnalysis } from '../../apiHelper';
import { AlertTriangle, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';

const formatCurrency = (val) => {
    if (!val) return '₦0';
    if (val >= 1000000) return `₦${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `₦${(val / 1000).toFixed(1)}K`;
    return `₦${val.toLocaleString()}`;
};

const VendorPriceLagDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [filters, setFilters] = useState({
        min_transactions: 8,
        lag_threshold: 30,
    });

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                const result = await fetchVendorPriceLagAnalysis(filters);
                setData(result);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [filters]);

    const top = data?.top_exploiter || {};
    const exploiting = data?.exploiting_vendors || [];

    return (
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Vendor Price Lag Analysis</h2>
                    <p className="text-gray-500 text-xs mt-1">Detecting 0% interest loans from vendors</p>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3 bg-gray-50 px-5 py-3 rounded-2xl text-sm">
                        <label>Min Transactions:</label>
                        <input
                            type="number"
                            value={filters.min_transactions}
                            onChange={(e) => setFilters({ ...filters, min_transactions: parseInt(e.target.value) })}
                            className="w-16 bg-transparent outline-none font-medium"
                        />
                    </div>

                    <div className="flex items-center gap-3 bg-gray-50 px-5 py-3 rounded-2xl text-sm">
                        <label>Lag Threshold (days):</label>
                        <input
                            type="number"
                            value={filters.lag_threshold}
                            onChange={(e) => setFilters({ ...filters, lag_threshold: parseInt(e.target.value) })}
                            className="w-16 bg-transparent outline-none font-medium"
                        />
                    </div>

                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-3 bg-[#4285F4] text-white rounded-2xl hover:bg-blue-600 transition font-medium flex items-center gap-2"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* Top Exploiter Highlight */}
            {top.vendor_name && (
                <div className="mb-8 bg-red-50 border border-red-200 rounded-3xl p-7">
                    <div className="flex items-start gap-4">
                        <AlertTriangle className="text-red-600 mt-1" size={28} />
                        <div className="flex-1">
                            <h3 className="font-semibold text-red-800 text-lg">Top Exploiter Detected</h3>
                            <p className="mt-2 text-red-700">
                                <strong>{top.vendor_name}</strong> on SKU <strong>{top.sku}</strong> ({top.sku_name})
                            </p>
                            <div className="mt-4 grid grid-cols-3 gap-6 text-sm">
                                <div>
                                    <span className="text-gray-500">Hike Lag:</span>
                                    <span className="font-semibold ml-2">{top.avg_hike_lag_days} days</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Drop Lag:</span>
                                    <span className="font-semibold ml-2 text-red-600">{top.avg_drop_lag_days} days</span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Asymmetry:</span>
                                    <span className="font-semibold ml-2">{top.asymmetry_score}</span>
                                </div>
                            </div>
                            <p className="mt-4 text-red-700 font-medium">
                                Value Affected: {formatCurrency(top.total_value_affected)}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Recommendation */}
            {data?.recommendation && (
                <div className="mb-8 bg-amber-50 border border-amber-200 rounded-3xl p-6">
                    <p className="text-amber-800 leading-relaxed">{data.recommendation}</p>
                </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="bg-white border border-gray-200 p-6 rounded-3xl">
                    <p className="text-gray-500 text-sm">Exploiting Vendors</p>
                    <p className="text-4xl font-bold mt-2">{data?.summary?.total_exploiting_vendors || 0}</p>
                </div>
                <div className="bg-white border border-gray-200 p-6 rounded-3xl">
                    <p className="text-gray-500 text-sm">Total Value Affected</p>
                    <p className="text-4xl font-bold mt-2 text-red-600">
                        {formatCurrency(data?.summary?.total_value_affected || 0)}
                    </p>
                </div>
                <div className="bg-white border border-gray-200 p-6 rounded-3xl">
                    <p className="text-gray-500 text-sm">Avg Asymmetry Score</p>
                    <p className="text-4xl font-bold mt-2">{data?.summary?.avg_asymmetry_score || 0}</p>
                </div>
            </div>

            {/* Exploiting Vendors Table */}
            <div className="flex-1 bg-white border border-gray-100 rounded-3xl p-8 overflow-auto">
                <h3 className="font-semibold mb-6">All Detected Price Lag Cases</h3>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b text-left">
                            <th className="py-4 font-medium text-gray-500">Vendor</th>
                            <th className="py-4 font-medium text-gray-500">SKU</th>
                            <th className="py-4 text-right font-medium text-gray-500">Hike Lag</th>
                            <th className="py-4 text-right font-medium text-gray-500">Drop Lag</th>
                            <th className="py-4 text-right font-medium text-gray-500">Asymmetry</th>
                            <th className="py-4 text-right font-medium text-gray-500">Value Affected</th>
                        </tr>
                    </thead>
                    <tbody>
                        {exploiting.map((item, i) => (
                            <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                                <td className="py-4 font-medium">{item.vendor_name}</td>
                                <td className="py-4 text-gray-600">{item.sku} — {item.sku_name}</td>
                                <td className="py-4 text-right">{item.avg_hike_lag_days} days</td>
                                <td className="py-4 text-right text-red-600 font-medium">{item.avg_drop_lag_days} days</td>
                                <td className="py-4 text-right font-semibold">{item.asymmetry_score}</td>
                                <td className="py-4 text-right font-semibold">{formatCurrency(item.total_value_affected)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {exploiting.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                        No significant price lag exploitation detected in the current period.
                    </div>
                )}
            </div>
        </div>
    );
};

export default VendorPriceLagDashboard;